/**
 * Component: Product info
 * Danish Modern L.A. Shopify Theme
 *
 * Keeps the buy column in step with the variant picker. All prices arrive
 * pre-formatted from Liquid (see the data-variant-json script in
 * main-product.liquid), so this never formats money itself.
 *
 * Without JavaScript the form still submits the first available variant.
 */

if (!customElements.get('product-info')) {
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variant-json]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.selects = Array.from(this.querySelectorAll('[data-option-index]'));
      if (this.selects.length > 0) {
        this.onChange = () => this.update();
        this.selects.forEach((select) => select.addEventListener('change', this.onChange));
      } else if (this.variants.length > 0) {
        const currentVariant = this.variants[0];
        if (currentVariant) {
          const isSold = Boolean(currentVariant.is_sold || !currentVariant.available);
          const priceEl = this.querySelector('[data-variant-price]');
          if (priceEl) {
            priceEl.hidden = isSold;
            if (isSold) {
              priceEl.classList.add('hidden');
              priceEl.style.display = 'none';
            }
          }
          const soldBadge = this.querySelector('[data-sold-badge]');
          if (soldBadge && isSold) {
            soldBadge.hidden = false;
            soldBadge.style.display = '';
          }
          this.renderButton(currentVariant);
        }
      }
    }

    disconnectedCallback() {
      this.selects?.forEach((select) => select.removeEventListener('change', this.onChange));
    }

    get selectedOptions() {
      return this.selects
        .sort((a, b) => a.dataset.optionIndex - b.dataset.optionIndex)
        .map((select) => select.value);
    }

    update() {
      const options = this.selectedOptions;
      const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));

      this.renderButton(variant);
      if (!variant) return;

      const isSold = Boolean(variant.is_sold || !variant.available);

      this.setValue('[data-variant-id]', variant.id);
      // Keep the inventory cap in sync so cart-limit.js caps adds correctly.
      this.querySelectorAll('[data-variant-id]').forEach((el) => {
        if (typeof variant.inventory === 'number') {
          el.setAttribute('data-variant-inventory', String(variant.inventory));
        }
      });
      this.setText('[data-variant-price]', variant.price);
      this.setText('[data-variant-installment]', variant.installment);
      this.setText('[data-variant-sku]', variant.sku);

      const priceEl = this.querySelector('[data-variant-price]');
      if (priceEl) {
        priceEl.hidden = isSold;
        if (isSold) {
          priceEl.classList.add('hidden');
          priceEl.style.display = 'none';
        } else {
          priceEl.classList.remove('hidden');
          priceEl.style.display = '';
        }
      }

      const compare = this.querySelector('[data-variant-compare]');
      if (compare) {
        compare.hidden = isSold || !variant.compare_at_price;
        if (isSold || !variant.compare_at_price) {
          compare.style.display = 'none';
        } else {
          compare.style.display = '';
          compare.textContent = variant.compare_at_price || '';
        }
      }

      const soldBadge = this.querySelector('[data-sold-badge]');
      if (soldBadge) {
        soldBadge.hidden = !isSold;
        if (isSold) {
          soldBadge.style.display = '';
        } else {
          soldBadge.style.display = 'none';
        }
      }

      const installments = this.querySelector('[data-variant-installments]');
      if (installments) {
        installments.hidden = isSold;
        if (isSold) {
          installments.style.display = 'none';
        } else {
          installments.style.display = '';
        }
      }

      const taxNote = this.querySelector('[data-variant-tax-note]');
      if (taxNote) {
        taxNote.hidden = isSold;
        if (isSold) {
          taxNote.style.display = 'none';
        } else {
          taxNote.style.display = '';
        }
      }

      const quantityEl = this.querySelector('[data-variant-quantity]');
      if (quantityEl) {
        quantityEl.hidden = isSold;
        if (isSold) {
          quantityEl.style.display = 'none';
        } else {
          quantityEl.style.display = '';
        }
      }

      const dynamicCheckout = this.querySelector('[data-dynamic-checkout]');
      if (dynamicCheckout) {
        dynamicCheckout.hidden = isSold;
        if (isSold) {
          dynamicCheckout.style.display = 'none';
        } else {
          dynamicCheckout.style.display = '';
        }
      }

      // Shareable URL for the chosen variant, without adding history entries.
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      document.dispatchEvent(
        new CustomEvent('product:variant-change', {
          detail: { sectionId: this.dataset.sectionId, galleryId: this.dataset.galleryId, variant, mediaId: variant.media_id },
        })
      );
    }

    renderButton(variant) {
      const button = this.querySelector('[data-add-to-cart]');
      const label = this.querySelector('[data-add-to-cart-text]');
      if (!button || !label) return;

      const isSold = variant ? Boolean(variant.is_sold || !variant.available) : false;
      const available = Boolean(variant && variant.available && !isSold);
      button.disabled = !available;
      button.classList.toggle('is-sold', isSold);

      if (!variant) label.textContent = button.dataset.textUnavailable;
      else if (isSold) label.textContent = button.dataset.textSoldOut;
      else label.textContent = button.dataset.textAdd;
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach((el) => {
        el.textContent = value ?? '';
      });
    }

    setValue(selector, value) {
      this.querySelectorAll(selector).forEach((el) => {
        el.value = value;
      });
    }
  }

  customElements.define('product-info', ProductInfo);
}
